import { useState } from 'react';
import { toast } from 'sonner';

import { useUploadForSellerMutation } from '@/client/api/assets/actions';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/** Add photos to a listing as its owner would: same upload pipeline and checks
 *  (the plan limit is an admin override), and the owner is told. */
const UploadForSellerDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const upload = useUploadForSellerMutation();
  const [eid, setEid] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [cover, setCover] = useState(false);
  const [reason, setReason] = useState('');
  const ready = eid && files.length && reason.trim();

  const go = async () => {
    try {
      await upload.mutateAsync({ eid: Number(eid), files, reason: reason.trim(), cover });
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'} to listing #${eid}`);
      setFiles([]);
      setReason('');
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Upload failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload for a seller</DialogTitle>
          <DialogDescription>Adds photos or videos to a listing as its owner. They go through the normal checks; the owner gets a notice with your reason.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Listing id" inputMode="numeric" value={eid} onChange={(e) => setEid(e.target.value.replace(/\D/g, ''))} />
        <input type="file" multiple accept="image/*,video/*" aria-label="Files to upload" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cover} onChange={(e) => setCover(e.target.checked)} /> Make the first one the cover</label>
        <Input placeholder="Reason (required, the owner sees it)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || upload.isPending} onClick={() => void go()}>{upload.isPending ? 'Uploading…' : `Upload ${files.length || ''}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadForSellerDialog;
